import { ReactNode } from 'react';
import { useAuthContext } from './auth';
import { GraphClientOptions, Payload } from './types';
import Cookies from 'js-cookie';
import jwtDecode from 'jwt-decode';
import {
  AnyVariables,
  Client,
  Operation,
  Provider,
  cacheExchange,
  createClient,
  errorExchange,
  fetchExchange,
  makeOperation,
} from 'urql';
import { authExchange } from '@urql/exchange-auth';

interface CreateGraphClientType {
  (options: GraphClientOptions, usage?: AppDetails): Client;
}

const CreateGraphClient: CreateGraphClientType = (
  options: GraphClientOptions,
  usage?: AppDetails,
) => {
  const { url, jwt, onError } = options;
  const { logout } = useAuthContext();
  const locale = Cookies.get('i18next') || 'de-DE';
  const headers: any = {
    'Content-Type': 'application/json',
    'Accept-Language': locale,
  };
  if (usage) {
    headers['graphql-client-name'] = usage.name;
    headers['graphql-client-version'] = usage.version;
  }
  return createClient({
    url,
    fetchOptions: () => ({ headers }),
    exchanges: [
      cacheExchange,
      authExchange(async () => {
        return {
          addAuthToOperation: (operation: Operation<any, AnyVariables>) => {
            if (!jwt) {
              return operation;
            }
            const fetchOptions =
              typeof operation.context.fetchOptions === 'function'
                ? operation.context.fetchOptions()
                : operation.context.fetchOptions || {};

            return makeOperation(operation.kind, operation, {
              ...operation.context,
              fetchOptions: {
                ...fetchOptions,
                headers: {
                  ...fetchOptions.headers,
                  Authorization: `Bearer ${jwt}`,
                },
              },
            });
          },
          willAuthError: () => {
            if (!jwt) return true;
            else {
              const payload = jwtDecode(jwt) as Payload;
              const expTime = new Date(payload?.exp * 1000);
              return expTime.getTime() - new Date().getTime() < 0;
            }
          },
          refreshAuth: async () => {
            logout();
          },
          didAuthError: (e) => {
            console.log('didAuthError', e);
            return e.graphQLErrors.some(
              (e) => e.extensions?.code === 'FORBIDDEN',
            );
          },
        };
      }),
      errorExchange({
        onError: (error) => {
          console.log('errorExchange', error.response); // keep here for debugging for now
          if (error?.response?.code >= 400)
            onError({
              code: error.response.code,
              message: error.response.statusText,
            });
          else if (error?.graphQLErrors.length)
            onError({
              code: error.response.code,
              message: error?.graphQLErrors.toString(),
            });
          else console.error('Unrecognized error', error);
        },
      }),
      fetchExchange,
    ],
  });
};

type AppDetails = {
  name: string;
  version: string;
};

type Props = {
  graphUrl: string;
  usage?: AppDetails;
  children: ReactNode;
};

function UrqlProvider({ graphUrl, children, usage }: Props) {
  const { jwt } = useAuthContext();
  const client = CreateGraphClient(
    {
      url: graphUrl,
      jwt: jwt as string,
      // onError: ({ code, message }) => {
      onError: (error) => {
        const { code, message } = error;
        console.log(error);
        if (code === 400) {
          window.location.href = `${window.location.origin}/login`;
        } else {
          console.error(`api err code ${code}:`, message);
        }
      },
    },
    usage,
  );
  return <Provider value={client}>{children}</Provider>;
}

export { CreateGraphClient, UrqlProvider };
